"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { optionType, stateApiType } from "utils";
import { fetchCountryStates } from "../services/pricesService";

type UseCountryStatesResult = {
  /** True until the list of the selected country is known, including the very first render. */
  isLoading: boolean;
  /** Subdivisions of the selected country, empty when the country has no predefined list. */
  states: optionType[];
  /** True while the country is known to have a list, so the form should render a dropdown. */
  hasStates: boolean;
};

type LoadedStates = { code: string; states: stateApiType[] };

// Subdivision lists are static, so one fetch per country is enough for the whole session.
const cache = new Map<string, stateApiType[]>();

export function useCountryStates(countryCode?: string): UseCountryStatesResult {
  const code = countryCode?.trim().toUpperCase() || undefined;
  const [loaded, setLoaded] = useState<LoadedStates | null>(() => {
    const cached = code ? cache.get(code) : undefined;
    return code && cached ? { code, states: cached } : null;
  });
  const requestedCode = useRef<string | undefined>(undefined);

  useEffect(() => {
    requestedCode.current = code;

    if (!code) {
      setLoaded(null);
      return;
    }

    const cached = cache.get(code);
    if (cached) {
      setLoaded({ code, states: cached });
      return;
    }

    fetchCountryStates(code)
      .then((resp: stateApiType[]) => {
        const result = resp ?? [];
        cache.set(code, result);
        // Ignore a response that arrived after the user picked another country.
        if (requestedCode.current === code) {
          setLoaded({ code, states: result });
        }
      })
      .catch(() => {
        // A missing list is not an error for the user: the field falls back to free text.
        if (requestedCode.current === code) {
          setLoaded({ code, states: [] });
        }
      });
  }, [code]);

  // Right after the country changes the previous country's list is still in state. Reporting it
  // would let the form match, or wipe, the state value against the wrong country.
  const current = code && loaded?.code === code ? loaded.states : null;

  const options = useMemo(
    () =>
      (current ?? []).map((item) => ({
        label: item.friendlyName,
        value: item.isoCode,
      })),
    [current]
  );

  return {
    isLoading: !!code && current === null,
    states: options,
    hasStates: options.length > 0,
  };
}
